import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, OnChanges, ViewEncapsulation } from '@angular/core';
import { FormControl, Validators, ValidatorFn } from '@angular/forms';
import { TooltipService } from '@irb-common/components/tooltip/tooltip.service';
import { conditionalValidator } from '@irb-common/validators/conditionalValidator';
import { startCharacterValidator } from '@irb-common/validators/startCharacterValidator';
import { ValidatorForDollarAmounts } from '@irb/irb-common/validators/startCharacterValidatorForDollorAmounts';
import {debounceTime, distinctUntilChanged} from "rxjs/operators";

@Component({ standalone: false,
  selector: 'irb-validation-text-box',
  templateUrl: './validation-text-box.component.html',
  styleUrls: ['./validation-text-box.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ValidationTextBoxComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild("inputField") inputField: ElementRef;
  @Input() value: string;
  @Output() change: EventEmitter<string> = new EventEmitter<string>();
  @Output() blur: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() valueChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() applyChange: EventEmitter<any> = new EventEmitter<any>();
  @Input() regExp: RegExp;
  @Input() invalidMessage: string = "Invalid input";
  @Input() placeholder: string = "";
  @Input() min: number = 0;
  @Input() max: number = 32768;
  @Input() width: string = "80px";
  @Input() disabled: boolean = false;
  @Input() autofocus: boolean = false;
  @Input() prefix: string;
  @Input() suffix: string;
  @Input() minConstraint: string;
  @Input() maxConstraint: string;
  @Input() invalidMinConstraintMessage: string = "";
  @Input() invalidMaxConstraintMessage: string = "";
  @Input() allowNegativeValues:boolean = false;
  @Input() isRequired:boolean = false;
  patternValidator: ValidatorFn;
  tooltipMessage: string;
  inputControl: FormControl;

  constructor(private tts: TooltipService) {}

  ngOnInit(): void {
    const v = [];

    if(this.min) {
      v.push(Validators.minLength(this.min));
    }

    if(this.max) {
      v.push(Validators.maxLength(this.max));
    }

    v.push(conditionalValidator(() => this.maxConstraint, (c: FormControl) => {
      if(c.value) {
        const v = this.removePrefixAndSuffix(c.value);
        const m = this.removePrefixAndSuffix(this.maxConstraint);
        const a = parseFloat(v);
        const b = parseFloat(m);
        const r = (a > b) ? {'violatesMaxConstraint': {value: c.value}} : null;
        return r;
      }

      return null;
    }));

    v.push(conditionalValidator(() => this.minConstraint, (c: FormControl) => {
      if(c.value) {
        const v = this.removePrefixAndSuffix(c.value);
        const m = this.removePrefixAndSuffix(this.minConstraint);
        const a = parseFloat(v);
        const b = parseFloat(m);
        const r = (a < b) ? {'violatesMinConstraint': {value: c.value}} : null;
        return r;
      }

      return null;
    }));

    if(this.regExp) {
      this.patternValidator = Validators.pattern(this.regExp);
      v.push(this.patternValidator);
    }

    if(this.isRequired) {
      v.push(Validators.required);
    }

    (this.allowNegativeValues) ?v.push(ValidatorForDollarAmounts()):v.push(startCharacterValidator());

    this.inputControl = new FormControl(this.value, v);

    this.inputControl.valueChanges.pipe(
      debounceTime(1000),
      distinctUntilChanged()
    ).subscribe(() => {
      this.onInputChange();
    });
  }

  ngAfterViewInit() {      
    if(this.autofocus) {
      this.inputField.nativeElement.focus();
    }
    
    this.change.emit(this.value);
    
    setTimeout(() => {
      this.inputControl.setValue(this.value);
      this.onInputChange();
    });
  }

  ngOnChanges(): void {
    if(this.inputControl) {
      if(this.regExp) {
        this.inputControl.removeValidators(this.patternValidator);
        this.patternValidator = Validators.pattern(this.regExp)
        this.inputControl.addValidators(this.patternValidator);
      } else {
        this.inputControl.removeValidators(this.patternValidator);
      }
            
      if(this.isRequired) {
        this.inputControl.addValidators(Validators.required);
      } else {
        this.inputControl.removeValidators(Validators.required);
      }

      this.inputControl.setValue(this.value);
      this.inputControl.updateValueAndValidity();
      this.onInputChange();
    }
  }

  onInputChange() {
    if(this.inputControl) {
      if(this.inputControl.invalid && this.inputControl.value !== null) {
        if(this.inputControl.hasError("violatesMinConstraint")) {
          this.tooltipMessage = this.invalidMinConstraintMessage;        
        } else if(this.inputControl.hasError("violatesMaxConstraint")) {
          this.tooltipMessage = this.invalidMaxConstraintMessage;  
        } else if(this.inputControl.hasError("InvalidStartCharacter")) {
          this.tooltipMessage = "Value can not be prefixed with +, -, = or @";
        } else {
          this.tooltipMessage = this.invalidMessage;
        }  
      } else {
        this.tooltipMessage = null;
        this.tts.remove();
      }
    }

    if(this.value != this.inputControl.value) {
      this.value = this.inputControl.value;
      this.change.emit(this.value);
      this.valueChange.emit(this.value);
    }
  }

  onBlur() {
    this.addPrefixAndSuffix();
    this.tts.remove();
    this.blur.emit(true);

    this.applyChange.emit({
      value: this.value,
      isValid: !this.inputControl.invalid
    });
  }

  onFocus() {    
    setTimeout(() => this.inputField.nativeElement.setSelectionRange(0, this.value?.toString().length));
  }

  blurInput() {
    this.inputField.nativeElement.blur();
  }

  getEnabledState() {
    if(this.disabled) {
      this.inputControl.disable();
    } else {
      this.inputControl.enable();
    }
  }

  addPrefixAndSuffix() {
    let v = this.inputControl.value?.toString();
    let sign = "";
      
    if(v && v.indexOf("-") !== -1) {
      v = v.replace(/-/g,'');
      sign = "-";
    }

    if(v && this.prefix && !v.startsWith(this.prefix)) {
      this.value = sign + this.prefix + v;
      this.inputControl.setValue(this.value);
      this.onInputChange();
    }

    if(v && this.suffix && !v.endsWith(this.suffix)) {
      this.value = sign + v + this.suffix;
      this.inputControl.setValue(this.value);
      this.onInputChange();
    }
  }

  removePrefixAndSuffix(v: string) {
    const p = (this.prefix) ? new RegExp(this.prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),"g") : '';
    const s = (this.suffix) ? new RegExp(this.suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),"g") : '';
    return v.replace(p,'').replace(s,'');
  }
}
